import React, { useState } from "react";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { RooflineMark } from "../shared/RooflineMark";
import { SectionHeader } from "../shared/SectionHeader";

// [Journey] [Trust] Oceanside's refined 6-stage process (client-supplied).
const processSteps = [{
  step: 1,
  title: "Initial consultation",
  headline: "Let’s understand what you’re looking to build.",
  description: "Every project starts with understanding your needs, your site and what you’re hoping to achieve.",
  items: ["Explore our home designs, or a custom option", "Discuss your block, lifestyle and must-haves", "Understand your budget and priorities", "Identify the best pathway for your project"]
}, {
  step: 2,
  title: "Design & costing",
  headline: "Creating your home and knowing where you stand.",
  description: "We refine the design to suit your block and prepare detailed costing before any building contract is signed.",
  items: ["Sign a pre-construction agreement to get started", "Site survey and soil test", "Concept design refined to suit your block", "Detailed costing so you know where you stand", "Working drawings prepared"]
}, {
  step: 3,
  title: "Contract & selections",
  headline: "Making it official, and making it yours.",
  description: "We want you to feel confident about what you’re signing and to love every finish you choose.",
  items: ["Review and sign your fixed-price building contract", "Choose your finishes, fixtures and colours with our specialist", "Confirm any variations before plans are locked in"]
}, {
  step: 4,
  title: "Final plans & approvals",
  headline: "Every detail confirmed before we build.",
  description: "Your plans are finalised and every approval is lodged, so construction can start with nothing left to chance.",
  items: ["Final plans updated with your selections", "Engineering and energy compliance completed", "Building permit and any council approvals lodged", "Finance and site start date confirmed"]
}, {
  step: 5,
  title: "Construction",
  headline: "From plans to reality.",
  description: "Each stage is quality checked and inspected, and we keep you informed about progress and upcoming milestones.",
  items: ["Site works and slab", "Walls, roof and lock-up", "Fit-out and finishes", "Regular updates and stage inspections"]
}, {
  step: 6,
  title: "Handover",
  headline: "Your home. Your keys. Your next chapter.",
  description: "We walk through your new home together, and we’re still here after you move in.",
  items: ["Practical completion inspection", "Keys, warranties and maintenance information", "Ongoing support after you move in", "Six-month maintenance period"]
}];
type BuildingJourneyProps = {
  detailed?: boolean;
};
export function BuildingJourney({
  detailed = false
}: BuildingJourneyProps) {
  const [active, setActive] = useState(0);
  const current = processSteps[active];
  if (detailed) {
    return <ol className="space-y-4" data-id="element-108" data-mp="2riqtxflbynmq">
        {processSteps.map(item => <li key={item.step} className="grid gap-6 rounded-[28px] border border-line bg-white p-7 md:p-10 lg:grid-cols-12" data-id="element-109" data-mp="2mk7597fcqxqo">
            <div className="lg:col-span-5" data-id="element-110" data-mp="2im9hm918cjpe">
              <span className="inline-flex items-center gap-3" data-id="element-111" data-mp="2dlu4nikv1e7y">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-charcoal font-heading text-[15px] font-semibold text-paper" data-id="element-112" data-mp="2dnevoy7yuf12">{item.step}</span>
                <span className="text-[15px] font-semibold text-slate" data-id="element-113" data-mp="36aj8vqh7pznx">{item.title}</span>
              </span>
              <h2 className="mt-4 text-[26px] font-semibold leading-tight tracking-tight" data-id="element-114" data-mp="31gwt3pfr5fnw">{item.headline}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-115" data-mp="2wi3q4qy3d71z">{item.description}</p>
            </div>
            <ul className="grid content-start gap-3 sm:grid-cols-2 lg:col-span-7" data-id="element-116" data-mp="3p8ltlt2q9afx">
              {item.items.map(point => <li key={point} className="flex items-start gap-3 text-[16px]" data-id="element-117" data-mp="38stcj9lg53we">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-118" data-mp="3drxp8a7kxjrh">
                    <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-119" data-mp="2ztg0c95xu17z" />
                  </span>
                  {point}
                </li>)}
            </ul>
          </li>)}
      </ol>;
  }
  return <section id="process" aria-labelledby="process-heading" className="bg-charcoal py-20 text-paper md:py-28" data-id="element-120" data-mp="3shvcmnxoq5fm">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-121" data-mp="f3zxcp0krfm">
        <SectionHeader id="process-heading" tone="dark" title="Know exactly what happens next." intro="Six clear stages from your first conversation to handover, so you always know what’s happening, what’s required and what’s coming next." data-id="element-122" data-mp="3iqh9m66iynhq" />

        <div role="tablist" aria-label="Building stages" className="relative mt-12 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6" data-id="element-123" data-mp="k2bpm6nlxj90">
          {processSteps.map((item, index) => {
          const selected = index === active;
          return <button key={item.step} role="tab" id={`stage-tab-${item.step}`} aria-selected={selected} aria-controls="stage-panel" tabIndex={selected ? 0 : -1} onClick={() => setActive(index)} onKeyDown={event => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const delta = event.key === "ArrowRight" ? 1 : -1;
            const nextIndex = (active + delta + processSteps.length) % processSteps.length;
            setActive(nextIndex);
            document.getElementById(`stage-tab-${processSteps[nextIndex].step}`)?.focus();
          }} className={`flex min-h-[56px] items-center gap-3 rounded-full border px-3 text-left transition-[background-color,border-color,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean ${selected ? "border-ocean bg-ocean text-charcoal" : "border-paper/20 text-paper/80 hover:border-paper/50 hover:text-paper"}`} data-id="element-124" data-mp="135vok4097frb" data-mpi={index}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-heading text-[14px] font-semibold ${selected ? "bg-charcoal text-paper" : "bg-paper/10"}`} data-id="element-125" data-mp="1f8ok7278rc8h">
                  {item.step}
                </span>
                <span className="text-[14px] font-semibold leading-tight md:text-[15px]" data-id="element-126" data-mp="1p51cbvn51i14">{item.title}</span>
              </button>;
        })}
        </div>

        <div id="stage-panel" role="tabpanel" aria-labelledby={`stage-tab-${current.step}`} className="mt-6 rounded-[28px] border border-paper/15 bg-[#332E2E] p-7 md:p-10" data-id="element-127" data-mp="wgnfhj7scrr5">
          <div className="grid gap-8 lg:grid-cols-12" data-id="element-128" data-mp="twz705y0fbhg">
            <div className="lg:col-span-5" data-id="element-129" data-mp="twh9z73iwt8h">
              <span className="inline-flex items-center gap-3 text-[14px] font-semibold text-ocean" data-id="element-130" data-mp="oxmilgrhpwcr">
                <RooflineMark data-id="element-131" data-mp="jyjmilthn0gx" /> Stage {current.step} of {processSteps.length}
              </span>
              <h3 className="mt-3 text-[26px] font-semibold leading-tight tracking-tight md:text-[30px]" data-id="element-132" data-mp="f0hsx6yfrir7">{current.headline}</h3>
              <p className="mt-3 text-[17px] leading-relaxed text-paper/75" data-id="element-133" data-mp="az6dfu9a0dwq">{current.description}</p>
            </div>
            <ul className="grid content-start gap-3 sm:grid-cols-2 lg:col-span-7" data-id="element-134" data-mp="66d7j322mzj2">
              {current.items.map(point => <li key={point} className="flex items-start gap-3 rounded-2xl border border-paper/10 px-4 py-3 text-[15px] text-paper/90" data-id="element-135" data-mp="3sewd5v789w1g">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ocean" aria-hidden="true" data-id="element-136" data-mp="3neqgg0m3pjci" />
                  {point}
                </li>)}
            </ul>
          </div>
          <div className="mt-8 flex items-center justify-between border-t border-paper/10 pt-6" data-id="element-137" data-mp="1b58hshbs11hj">
            <button type="button" onClick={() => setActive((active - 1 + processSteps.length) % processSteps.length)} className="min-h-[44px] rounded-full border border-paper/25 px-5 text-[14px] font-semibold text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-charcoal" data-id="element-138" data-mp="1g4uw6u3ccnrx">
              Previous
            </button>
            <button type="button" onClick={() => setActive((active + 1) % processSteps.length)} className="min-h-[44px] rounded-full bg-paper px-5 text-[14px] font-semibold text-charcoal transition-colors duration-150 ease-out hover:bg-ocean" data-id="element-139" data-mp="x5z01d7x9zbg">
              {active === processSteps.length - 1 ? "Back to start" : "Next stage"}
            </button>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-140" data-mp="dh86xt8ruqxp">
          <ButtonLink to="/our-process" variant="inverse" data-id="element-141" data-mp="3qyk0acu38f9o">
            See our full building process <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-142" data-mp="3lzqn6lipqqa1" />
          </ButtonLink>
        </div>
      </div>
    </section>;
}